import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const COOKIE_CONSENT_KEY = "valorform_cookie_consent";

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

export const CookieConsent = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay for smooth entrance
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    handleClose();
  };

  const acceptAll = () => {
    savePreferences({ essential: true, analytics: true, marketing: true });
  };

  const acceptEssential = () => {
    savePreferences({ essential: true, analytics: false, marketing: false });
  };

  const saveCustom = () => {
    savePreferences(preferences);
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 p-4 transition-all duration-300 ${
        isClosing ? "translate-y-full opacity-0" : "translate-y-0 opacity-100"
      }`}
    >
      <div className="container mx-auto max-w-4xl">
        <div className="bg-card border border-border rounded-xl shadow-2xl p-6 relative">
          <button
            onClick={acceptEssential}
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t("common.close")}
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex items-start gap-4">
            <div className="hidden sm:flex h-12 w-12 rounded-full bg-primary/10 items-center justify-center flex-shrink-0">
              <Cookie className="h-6 w-6 text-primary" />
            </div>

            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {t("cookies.consentTitle")}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t("cookies.consentDescription")}{" "}
                <Link to="/cookies" className="text-primary hover:underline">
                  {t("cookies.learnMore")}
                </Link>
              </p>

              {showDetails && (
                <div className="space-y-3 mb-4 p-4 bg-muted/50 rounded-lg animate-in slide-in-from-top-2 duration-200">
                  <label className="flex items-center gap-3 cursor-not-allowed">
                    <input
                      type="checkbox"
                      checked={preferences.essential}
                      disabled
                      className="h-4 w-4 rounded border-border"
                    />
                    <span className="text-sm text-foreground">
                      {t("cookies.essential")} ({t("cookies.required")})
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) =>
                        setPreferences({ ...preferences, analytics: e.target.checked })
                      }
                      className="h-4 w-4 rounded border-border accent-primary"
                    />
                    <span className="text-sm text-foreground">{t("cookies.analytics")}</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) =>
                        setPreferences({ ...preferences, marketing: e.target.checked })
                      }
                      className="h-4 w-4 rounded border-border accent-primary"
                    />
                    <span className="text-sm text-foreground">{t("cookies.marketing")}</span>
                  </label>
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                <Button onClick={acceptAll} className="shadow-accent-glow">
                  {t("cookies.acceptAll")}
                </Button>
                <Button variant="outline" onClick={acceptEssential}>
                  {t("cookies.acceptEssential")}
                </Button>
                {!showDetails ? (
                  <Button variant="ghost" onClick={() => setShowDetails(true)}>
                    {t("cookies.customize")}
                  </Button>
                ) : (
                  <Button variant="ghost" onClick={saveCustom}>
                    {t("cookies.savePreferences")}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
